/**
 * rxTemplates — THE single source for everything derived from a doctor's saved
 * Rx templates: the category buckets, the per-category ordering that every chip
 * row / count line / list grouping reads, and the D7 merge rules.
 *
 * Pure module — no React, no API, no DOM. Every surface (the five chip rows,
 * the All-templates list, the save form, the Rx Templates card) consumes THESE
 * functions; nothing re-derives a count, an ordering or a merge rule inline.
 *
 * Contract mirror (RX-TEMPLATES-BUILD §3, widened by build-plan.md §2 G6):
 *   category: "SYMPTOMS" | "DIAGNOSIS" | "MEDICINES" | "LAB_TESTS" | "SURGERIES" | null
 * where **null / absent = whole prescription** (every pre-existing row).
 */
import { normaliseRowKey, SURGERY_SAVE_DISABLED_HINT } from './rxMemory';

// ── Categories ───────────────────────────────────────────────────────
// Server values. `null` is NOT a 6th value — absence is the meaning — but the
// UI needs a stable bucket key for it, hence WHOLE_PRESCRIPTION (client-only,
// never sent to the API).
export const TEMPLATE_CATEGORIES = {
  SYMPTOMS: 'SYMPTOMS',
  DIAGNOSIS: 'DIAGNOSIS',
  MEDICINES: 'MEDICINES',
  LAB_TESTS: 'LAB_TESTS',
  SURGERIES: 'SURGERIES',
};

export const WHOLE_PRESCRIPTION = 'WHOLE_PRESCRIPTION';

/** Display / grouping order — one list, so rows, pills and chip rows can't diverge. */
export const TEMPLATE_CATEGORY_KEYS = [
  WHOLE_PRESCRIPTION,
  TEMPLATE_CATEGORIES.SYMPTOMS,
  TEMPLATE_CATEGORIES.DIAGNOSIS,
  TEMPLATE_CATEGORIES.MEDICINES,
  TEMPLATE_CATEGORIES.LAB_TESTS,
  TEMPLATE_CATEGORIES.SURGERIES,
];

/** Group headings in the list + the save form's category options (PO copy). */
export const TEMPLATE_CATEGORY_LABELS = {
  [WHOLE_PRESCRIPTION]: 'Whole prescription',
  [TEMPLATE_CATEGORIES.SYMPTOMS]: 'Symptoms',
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: 'Diagnosis',
  [TEMPLATE_CATEGORIES.MEDICINES]: 'Medicines',
  [TEMPLATE_CATEGORIES.LAB_TESTS]: 'Lab tests',
  [TEMPLATE_CATEGORIES.SURGERIES]: 'Surgeries',
};

/** Chip-row labels, one per placement (PO copy). */
export const TEMPLATE_ROW_LABELS = {
  [WHOLE_PRESCRIPTION]: 'Templates',
  [TEMPLATE_CATEGORIES.SYMPTOMS]: 'Symptom templates',
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: 'Diagnosis templates',
  [TEMPLATE_CATEGORIES.MEDICINES]: 'Medicine templates',
  [TEMPLATE_CATEGORIES.LAB_TESTS]: 'Lab test templates',
  [TEMPLATE_CATEGORIES.SURGERIES]: 'Surgery templates',
};

/**
 * How a category is NAMED inside a sentence — "See all 12 medicine templates",
 * "Showing 25 of 62 medicine templates…". One map, so the opener's accessible
 * name and the list's count line can never describe the same filter
 * differently. No category (the list's "All" filter) → plain `templates`.
 */
export const TEMPLATE_NOUNS = {
  [WHOLE_PRESCRIPTION]: 'whole-prescription templates',
  [TEMPLATE_CATEGORIES.SYMPTOMS]: 'symptom templates',
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: 'diagnosis templates',
  [TEMPLATE_CATEGORIES.MEDICINES]: 'medicine templates',
  [TEMPLATE_CATEGORIES.LAB_TESTS]: 'lab test templates',
  [TEMPLATE_CATEGORIES.SURGERIES]: 'surgery templates',
};

export const templateNoun = (category) => TEMPLATE_NOUNS[category] || 'templates';

/** "this category has nothing" copy inside the list (PO copy). */
export const TEMPLATE_CATEGORY_EMPTY_COPY = {
  [WHOLE_PRESCRIPTION]: 'No whole-prescription templates yet.',
  [TEMPLATE_CATEGORIES.SYMPTOMS]: 'No template has symptoms yet.',
  [TEMPLATE_CATEGORIES.DIAGNOSIS]: 'No template has a diagnosis yet.',
  [TEMPLATE_CATEGORIES.MEDICINES]: 'No template has medicines yet.',
  [TEMPLATE_CATEGORIES.LAB_TESTS]: 'No template has lab tests yet.',
  [TEMPLATE_CATEGORIES.SURGERIES]: 'No template has surgeries yet.',
};

/**
 * Chip-row copy (D18 / declutter variant A) — the row now lives ON the
 * section's header line, so every string it shows is here and nothing is
 * free-handed in a component. Designer's page-08 proposals, verbatim.
 */
export const TEMPLATE_ROW_COPY = {
  chipLabel: (name) => `+ ${name}`,
  chipAriaLabel: (template) => {
    const summary = templateContentSummary(template);
    return summary
      ? `Add "${template.name}" — ${summary}`
      : `Add "${template.name}"`;
  },
  openerLabel: (n) => `See all ${n}`,
  openerAriaLabel: (n, category) => `See all ${n} ${templateNoun(category)}`,
  saveLabel: 'Save…',
  zeroLine: 'Nothing saved yet — write the prescription as usual, then save any part of it to reuse next time.',
  // The Replace path only (D6): a whole-prescription template does not merge
  // into one section, it stands in for everything already written.
  wholePrescriptionReplaceLine: 'Full prescription — replaces what you have written.',
  // Shown under the save form's category step when it was opened from a
  // section's Save… (the answer the doctor already gave by where they tapped).
  categoryPreselectedHint: (category) =>
    `Set to ${TEMPLATE_CATEGORY_LABELS[category]} because you saved from the ${TEMPLATE_CATEGORY_LABELS[category]} section — you can change it.`,
  // Appended to the section's existing (i) tooltip — only for a role that can
  // actually use the controls (D12).
  sectionTooltipSentence: 'Choose a saved template to add it — nothing you have written is replaced, and you can undo.',
};

/**
 * The pad's symptom picker (D23 / S14). PO-ratified, verbatim.
 *
 * D25 — `groupSets` ("My symptom sets") is RETIRED: a SYMPTOMS-category
 * template IS a symptom set, so the picker's template group now reads the same
 * `TEMPLATE_ROW_LABELS.SYMPTOMS` heading the other three fields use. One
 * heading map for all four dropdowns; no per-field free-handed heading.
 */
export const SYMPTOM_PICKER_COPY = {
  inputPlaceholder: 'Type a symptom and press Enter',
  groupYours: 'Your symptoms',
  groupSpecialisation: (specialisation) => `${specialisation} symptoms`,
  groupCommon: 'Common symptoms',
  yoursEmpty: 'Symptoms you add will show up here next time.',
};

/**
 * The pad's attached-images section (D22 restyle). Same place as the pad's
 * other copy — one constant per string, none in a component.
 */
export const IMAGES_SECTION_COPY = {
  // The ratified label is one string; it is RENDERED in two spans so the count
  // can be the muted secondary the pad language asks for — the concatenated
  // text is exactly `label(n, max)`.
  label: (n, max) => `Images (${n} of ${max})`,
  labelWord: 'Images',
  labelCount: (n, max) => `(${n} of ${max})`,
  addButton: '+ Add image',
  addButtonTitle: 'Add an image — or drag one onto this section',
  maxReason: (max) => `You can attach up to ${max} images.`,
  replaceButton: 'Replace',
  replaceTitle: 'Replace this image (applies when you save)',
  badType: (name) => `"${name}" is not a JPG, PNG or WebP image, so it was not added.`,
  tooBig: (name) => `"${name}" is larger than 5 MB, so it was not added.`,
  uploadFailed: 'The prescription was saved, but the image could not be uploaded. Press Generate Prescription again to retry.',
};

/**
 * P4 — the capped C/O quick-pill row. Lives here with the pad's other copy so
 * there is one place to change a string, not one per component.
 */
export const C_O_MORE_PILLS = {
  rowLabel: 'Recent',
  more: (n) => `+${n} more`,
  moreAria: (n) => `Show ${n} more recent complaints`,
  less: 'Show fewer',
  lessAria: 'Show fewer recent complaints',
};

/**
 * Templates strip copy (D20 / variant C) — the tab bar's visible label and its
 * accessible name, both built from the maps above so a tab, its opener and the
 * list's count line can never name the same category differently.
 */
export const TEMPLATE_STRIP_COPY = {
  heading: TEMPLATE_ROW_LABELS[WHOLE_PRESCRIPTION],
  regionAriaLabel: 'Templates — add a saved template to this prescription',
  tabListAriaLabel: 'Template categories',
  tabLabel: (category, n) => `${TEMPLATE_CATEGORY_LABELS[category]} · ${n}`,
  tabAriaLabel: (category, n) => {
    const noun = templateNoun(category);
    const capped = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;
    return n === 0 ? `${capped} — none saved yet` : `${capped} — ${n} saved`;
  },
};

/**
 * What a row's `Save…` would save, and why it is disabled when it is — derived
 * from the SAME pad snapshot the save form is prefilled from, so the button can
 * never promise something the form would not carry.
 *
 * @returns {{ disabled: boolean, title: string }}
 */
export function templateSaveAffordance(category, pad = {}) {
  const medicines = (pad.medicines || []).length;
  const symptoms = (pad.symptoms || []).length;
  const tests = (pad.investigations || []).length;
  const surgeries = (pad.advisedSurgeries || []).length;
  const diagnosis = String(pad.diagnosis || '').trim();

  if (category === TEMPLATE_CATEGORIES.SYMPTOMS) {
    return symptoms
      ? { disabled: false, title: 'Save these symptoms as a template' }
      : { disabled: true, title: 'Add a symptom first' };
  }
  if (category === TEMPLATE_CATEGORIES.DIAGNOSIS) {
    return diagnosis
      ? { disabled: false, title: 'Save this diagnosis as a template' }
      : { disabled: true, title: 'Write the diagnosis first' };
  }
  if (category === TEMPLATE_CATEGORIES.MEDICINES) {
    return medicines
      ? {
        disabled: false,
        // Singular reads as a sentence, not a count (PO): "the medicine".
        title: medicines === 1
          ? 'Save the medicine on this prescription as a template'
          : `Save the ${plural(medicines, 'medicine')} on this prescription as a template`,
      }
      : { disabled: true, title: 'Add a medicine first' };
  }
  if (category === TEMPLATE_CATEGORIES.LAB_TESTS) {
    return tests
      ? { disabled: false, title: 'Save these tests as a template' }
      : { disabled: true, title: 'Add a lab test first' };
  }
  if (category === TEMPLATE_CATEGORIES.SURGERIES) {
    return surgeries
      ? { disabled: false, title: 'Save these surgeries as a template' }
      : { disabled: true, title: SURGERY_SAVE_DISABLED_HINT };
  }
  const anything = medicines || symptoms || tests || surgeries || diagnosis
    || String(pad.chiefComplaint || '').trim() || String(pad.doctorNotes || '').trim()
    || !!pad.followUpRequired;
  return anything
    ? { disabled: false, title: 'Save this whole prescription as a template' }
    : { disabled: true, title: 'Write the prescription first' };
}

/**
 * The list fetch failed. ONE pair of strings for every surface that has to say
 * so — the strip (where the doctor is looking) and the list itself — so a
 * failure is never reported as "Nothing saved yet" (S2 AC8).
 */
export const TEMPLATE_ERROR_COPY = {
  line: 'Templates did not load — you can still write the prescription.',
  retry: 'Try again',
};

/**
 * Delete copy (D16 / S12 `DELETE_*`) — ONE set of strings for BOTH surfaces
 * that can delete a template: the pad's All-templates list and the Rx
 * Templates card. Neither surface writes its own variant.
 */
export const TEMPLATE_DELETE_COPY = {
  rowActionLabel: 'Delete',
  rowAriaLabel: (name) => `Delete template "${name}"`,
  confirmTitle: 'Delete this template?',
  confirmBody: (name) =>
    `"${name}" will be removed from your templates. Prescriptions you have already written are not affected.`,
  confirmDangerButton: 'Delete template',
  confirmKeepButton: 'Keep it',
  successToast: (name) => `Deleted "${name}".`,
  failureToast: 'Could not delete the template. It is still in your templates — try again.',
};

/** 4 most-used chips per row (D14); 25 rows before the count line (D6). */
export const TEMPLATE_CHIP_COUNT = 4;
export const TEMPLATE_LIST_ROW_CAP = 25;

// ── Sort (D26 / S16) ────────────────────────────────────────────────
/**
 * The four orders, and the ONE comparator set behind them. The All-templates
 * list, the Templates strip's chips and the on-focus dropdown rows all read
 * `selectByCategory(templates, sort)` — there is no second ordering anywhere,
 * so the three surfaces cannot drift.
 */
export const TEMPLATE_SORTS = {
  NEWEST: 'newest',
  MOST_USED: 'most_used',
  LAST_USED: 'last_used',
  NAME: 'name',
};

/** Option order in the control. `NEWEST` is the default (D26). */
export const TEMPLATE_SORT_KEYS = [
  TEMPLATE_SORTS.NEWEST,
  TEMPLATE_SORTS.MOST_USED,
  TEMPLATE_SORTS.LAST_USED,
  TEMPLATE_SORTS.NAME,
];

export const DEFAULT_TEMPLATE_SORT = TEMPLATE_SORTS.NEWEST;

/** PO copy, verbatim (stories.md § Copy — SORT_*). */
export const TEMPLATE_SORT_COPY = {
  label: 'Sort by',
  option: {
    [TEMPLATE_SORTS.NEWEST]: 'Newest first',
    [TEMPLATE_SORTS.MOST_USED]: 'Most used',
    [TEMPLATE_SORTS.LAST_USED]: 'Last used',
    [TEMPLATE_SORTS.NAME]: 'A to Z',
  },
};

const text = (v) => String(v ?? '');
const byNameAsc = (a, b) => text(a.name).toLowerCase().localeCompare(text(b.name).toLowerCase());
/** Newest first, and the tie-break every other order falls back to (stable). */
const byNewest = (a, b) => {
  const ca = text(a.created_at);
  const cb = text(b.created_at);
  if (ca !== cb) return cb.localeCompare(ca);
  return byNameAsc(a, b);
};

const TEMPLATE_COMPARATORS = {
  [TEMPLATE_SORTS.NEWEST]: byNewest,
  [TEMPLATE_SORTS.MOST_USED]: (a, b) => {
    const ua = Number(a.usage_count) || 0;
    const ub = Number(b.usage_count) || 0;
    return ua === ub ? byNewest(a, b) : ub - ua;
  },
  // Never-used rows fall to the END (order is the message — no "—" placeholder),
  // and `last_used_at` is absent on every legacy row, so the null case is the
  // common one, not the edge case.
  [TEMPLATE_SORTS.LAST_USED]: (a, b) => {
    const la = text(a.last_used_at);
    const lb = text(b.last_used_at);
    if (!la && !lb) return byNewest(a, b);
    if (!la) return 1;
    if (!lb) return -1;
    return la === lb ? byNewest(a, b) : lb.localeCompare(la);
  },
  [TEMPLATE_SORTS.NAME]: (a, b) => {
    const n = byNameAsc(a, b);
    return n === 0 ? byNewest(a, b) : n;
  },
};

/** An unknown/absent sort key always reads as the default — never an error. */
export function normaliseTemplateSort(sort) {
  return TEMPLATE_COMPARATORS[sort] ? sort : DEFAULT_TEMPLATE_SORT;
}

// The doctor's own preference, per device. Namespaced by member so two doctors
// (or two organisations) sharing a browser never read each other's choice.
const SORT_STORAGE_PREFIX = 'ak_rx_template_sort_';
const sortStorageKey = (memberId) => `${SORT_STORAGE_PREFIX}${memberId || 'anon'}`;

/** Stored choice, or the default. Storage may be absent, full or poisoned. */
export function readTemplateSort(memberId) {
  try {
    return normaliseTemplateSort(window.localStorage.getItem(sortStorageKey(memberId)));
  } catch {
    return DEFAULT_TEMPLATE_SORT;
  }
}

/** Remember the choice. A failure to store is never surfaced to the doctor. */
export function writeTemplateSort(memberId, sort) {
  try {
    window.localStorage.setItem(sortStorageKey(memberId), normaliseTemplateSort(sort));
  } catch { /* private mode / quota — the session's choice still stands */ }
}

/** Bucket key for one template — an unknown/absent category reads as whole Rx. */
export function categoryKeyOf(template) {
  const c = template?.category;
  return c && TEMPLATE_CATEGORIES[c] ? c : WHOLE_PRESCRIPTION;
}

// ── THE selector ─────────────────────────────────────────────────────
/**
 * selectByCategory — the ONE derivation of "which templates, in what order,
 * and how many" per category. Ranking is per category (D14), so a heavily used
 * medicines template can never push a whole-Rx template off the top row.
 *
 * D26 — the order is the doctor's chosen `sort` (default `newest`), applied
 * here and nowhere else: the list, the strip's chips and the on-focus rows all
 * come through this function, so they cannot disagree.
 *
 * @param {Array} templates
 * @param {string} sort  one of TEMPLATE_SORTS; anything else reads as newest
 * @returns {{ byCategory: Object, top: Object, counts: Object, total: number, sort: string }}
 */
export function selectByCategory(templates, sort = DEFAULT_TEMPLATE_SORT) {
  const byCategory = {};
  for (const key of TEMPLATE_CATEGORY_KEYS) byCategory[key] = [];
  for (const t of templates || []) {
    if (!t) continue;
    byCategory[categoryKeyOf(t)].push(t);
  }
  const key_ = normaliseTemplateSort(sort);
  const comparator = TEMPLATE_COMPARATORS[key_];
  const top = {};
  const counts = {};
  let total = 0;
  for (const key of TEMPLATE_CATEGORY_KEYS) {
    byCategory[key].sort(comparator);
    counts[key] = byCategory[key].length;
    top[key] = byCategory[key].slice(0, TEMPLATE_CHIP_COUNT);
    total += counts[key];
  }
  return { byCategory, top, counts, total, sort: key_ };
}

// ── The per-FIELD projection (D25 / variant F) ───────────────────────
/**
 * ≤3 template rows in a field's dropdown; 2 on a narrow host (the accordion,
 * mobile, the two-column pad's left column). A dropdown NEVER grows with the
 * data — `See all n` opens the All-templates list, which owns search and the
 * 25-row cap.
 */
export const FIELD_TEMPLATE_ROW_CAP = 3;
export const FIELD_TEMPLATE_ROW_CAP_NARROW = 2;

/**
 * D41 / S17 — the one sentence that teaches the double-click shortcut. Shown in
 * a field's EXISTING hint line, only while that input is empty AND focused, and
 * only on a fine pointer. PO-ratified, verbatim.
 */
export const DOUBLE_CLICK_HINT = 'Double-click to see all templates';

/** The ratified sub-line under a template row (shipped with the D10 card). */
export const FIELD_TEMPLATE_ROW_SUBLINE = 'From your templates';

/** What a template of THIS category contributes to THIS field. */
export function templateFieldItems(template, category) {
  if (!template) return [];
  if (category === TEMPLATE_CATEGORIES.SYMPTOMS) {
    return (template.symptoms || []).filter(Boolean).map(String);
  }
  if (category === TEMPLATE_CATEGORIES.MEDICINES) {
    return (template.medicines || []).map((m) => m.medicine || m.medicine_input).filter(Boolean);
  }
  if (category === TEMPLATE_CATEGORIES.LAB_TESTS) {
    return (template.investigations || []).filter(Boolean);
  }
  if (category === TEMPLATE_CATEGORIES.DIAGNOSIS) {
    return [template.diagnosis].filter(Boolean);
  }
  if (category === TEMPLATE_CATEGORIES.SURGERIES) {
    return (template.advised_surgeries || []).filter(Boolean).map(String);
  }
  return [];
}

/** The counted noun in a row title — "Fever meds · 3 medicines". */
const FIELD_ROW_NOUN = {
  [TEMPLATE_CATEGORIES.SYMPTOMS]: 'symptom',
  [TEMPLATE_CATEGORIES.MEDICINES]: 'medicine',
  [TEMPLATE_CATEGORIES.LAB_TESTS]: 'lab test',
  [TEMPLATE_CATEGORIES.SURGERIES]: 'surgery',
};

/**
 * The two lines of a template row, from ONE place so all four dropdowns
 * describe the same template the same way. A DIAGNOSIS template carries no
 * count (it is one line of text) — its sub-line is the text that will actually
 * be appended, which is what the doctor has to see before pressing Enter.
 */
export function fieldTemplateRowText(template, category) {
  const name = String(template?.name || '');
  const items = templateFieldItems(template, category);
  if (category === TEMPLATE_CATEGORIES.DIAGNOSIS) {
    return { title: name, subline: items[0] || FIELD_TEMPLATE_ROW_SUBLINE };
  }
  const noun = FIELD_ROW_NOUN[category];
  return {
    title: noun && items.length ? `${name} · ${plural(items.length, noun)}` : name,
    subline: FIELD_TEMPLATE_ROW_SUBLINE,
  };
}

/**
 * fieldTemplateRows — THE per-field projection (D25 / F1). One selector behind
 * the group heading, the rows and `See all n` in all four dropdowns, reading
 * the SAME `selectByCategory` ordering the Templates strip's tab counts read,
 * so a field and the strip can never disagree about how many templates exist.
 *
 * Filtering is on the template's NAME and its CONTENTS (so "crp" finds a lab
 * template holding CRP), which is what "typing filters the templates and the
 * field's own rows together" means.
 *
 * `See all n` is offered whenever the MATCHED set is bigger than the rows on
 * screen — while typing too (bk-988). The cap is 3 (2 narrow), so a query
 * matching 6 templates showed 3 and hid the rest behind nothing at all; `n` is
 * the matched count, and opening the list carries the typed text with it.
 *
 * @param {Object} selection  the object `selectByCategory()` returns
 * @param {string} category   TEMPLATE_CATEGORIES.*
 * @param {string} query      what the doctor has typed in that field
 * @returns {{heading: string, rows: Array, total: number, seeAll: number}}
 */
export function fieldTemplateRows(selection, category, query = '', { narrow = false } = {}) {
  const all = selection?.byCategory?.[category] || [];
  const q = String(query || '').trim().toLowerCase();
  const matched = q
    ? all.filter((t) => [t.name, ...templateFieldItems(t, category)]
      .filter(Boolean)
      .some((hay) => String(hay).toLowerCase().includes(q)))
    : all;
  const cap = narrow ? FIELD_TEMPLATE_ROW_CAP_NARROW : FIELD_TEMPLATE_ROW_CAP;
  const rows = matched.slice(0, cap);
  return {
    heading: TEMPLATE_ROW_LABELS[category],
    rows,
    total: matched.length,
    seeAll: matched.length > rows.length ? matched.length : 0,
  };
}

/** Case-insensitive name search over the doctor's own templates. */
export function filterByQuery(templates, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return templates;
  return templates.filter((t) => String(t.name || '').toLowerCase().includes(q));
}

/**
 * buildListGroups — THE browse projection every long template list renders:
 * grouped by category in one order, searched, and truncated at 25 rows with the
 * count line's numbers. Used by the pad's All-templates list AND the Rx
 * Templates card, so the two can never disagree about what is shown.
 *
 * @param {Array} templates
 * @param {{ query?: string, category?: string|null, sort?: string }} opts  category null/absent = all
 * @returns {{ selection, groups: [{key, rows}], shown: number, matched: number }}
 */
export function buildListGroups(templates, { query = '', category = null, sort = DEFAULT_TEMPLATE_SORT } = {}) {
  const selection = selectByCategory(templates || [], sort);
  const keys = category ? [category] : TEMPLATE_CATEGORY_KEYS;
  let budget = TEMPLATE_LIST_ROW_CAP;
  let matched = 0;
  const groups = [];
  for (const key of keys) {
    const rows = filterByQuery(selection.byCategory[key] || [], query);
    matched += rows.length;
    if (rows.length === 0 || budget <= 0) continue;
    groups.push({ key, rows: rows.slice(0, budget) });
    budget -= Math.min(rows.length, budget);
  }
  return {
    selection,
    groups,
    shown: groups.reduce((n, g) => n + g.rows.length, 0),
    matched,
  };
}

/**
 * The count line, or null when there is nothing to say (PO copy). It names the
 * filter that is actually applied — "…of 62 medicine templates" — from the one
 * noun map, so it can never disagree with the opener that led here.
 */
export function listCountLine(shown, matched, category = null) {
  if (matched <= shown || shown === 0) return null;
  return `Showing ${shown} of ${matched} ${templateNoun(category)} — type to narrow.`;
}

/** Same doctor + same name (case-insensitive, trimmed) — the D9 prompt's test. */
export function findSameNameTemplate(templates, name, { excludeId = null } = {}) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return null;
  return (templates || []).find(
    (t) => t.template_id !== excludeId && String(t.name || '').trim().toLowerCase() === n
  ) || null;
}

// ── Merge (D7) ───────────────────────────────────────────────────────

const norm = (v) => String(v ?? '').toLowerCase().replace(/\s+/g, '');
const medicineKey = (m) => `${norm(m.medicine || m.medicine_input)}|${norm(m.strength)}`;

/** Template medicine → a prescription row, same shape every other fill path writes. */
function templateMedicineRow(m, seq) {
  return {
    _id: `tpl_${Date.now()}_${seq}_${Math.random().toString(36).slice(2, 6)}`,
    medicine: m.medicine || '',
    medicine_input: m.medicine_input || m.medicine || '',
    strength: m.strength || null,
    form: m.form || null,
    dosage_schedule: m.dosage_schedule || null,
    frequency: m.frequency || null,
    timing: m.timing || null,
    duration: m.duration || null,
    route: m.route || null,
    notes: m.notes || null,
    is_resolved: m.is_resolved ?? true,
  };
}

function emptySummary(name) {
  return {
    templateName: name,
    medicines: { added: 0, skipped: 0 },
    symptoms: { added: 0, skipped: 0 },
    tests: { added: 0, skipped: 0, skippedNames: [] },
    surgeries: { added: 0, skipped: 0 },
    diagnosis: null,          // 'added' | 'skipped' | null
    chiefComplaint: false,
    notes: false,
    followUp: false,
    changed: false,
  };
}

/**
 * mergeTemplate — D7, exactly. NOTHING is ever overwritten or cleared:
 *   symptoms          → union onto the selection (unknown ones become custom)
 *   diagnosis         → set if empty, else append ", <text>"; skipped if already present
 *   chief complaint   → fill ONLY if empty
 *   medicines         → append, skip duplicates on name+strength (case/space-insensitive)
 *   tests             → append, skip duplicates via the caller's normalizeLabName
 *   surgeries (FE-8)  → append, skip duplicates via `normaliseRowKey` (the SAME
 *                       row identity the Advised surgeries section itself uses)
 *   notes / follow-up → fill only if empty
 *   an empty part of the template touches nothing
 *
 * `lab_catalog_ids` are DELIBERATELY not read: Add never places a lab order
 * (D7). Only `Replace with selected` keeps that behaviour, and it does not go
 * through this function.
 *
 * @param {Object} pad   { medicines, investigations, symptoms, customSymptoms,
 *                         diagnosis, chiefComplaint, doctorNotes,
 *                         followUpRequired, followUpIn }
 * @param {Object} template
 * @param {Object} opts  { normalizeLabName, knownSymptoms }
 * @returns {{ next: Object, summary: Object }}
 */
export function mergeTemplate(pad, template, opts = {}) {
  const normalizeLabName = opts.normalizeLabName || norm;
  const knownSymptoms = opts.knownSymptoms instanceof Set
    ? opts.knownSymptoms
    : new Set(opts.knownSymptoms || []);

  const summary = emptySummary(template?.name || '');
  const next = {
    medicines: [...(pad.medicines || [])],
    investigations: [...(pad.investigations || [])],
    symptoms: [...(pad.symptoms || [])],
    customSymptoms: [...(pad.customSymptoms || [])],
    advisedSurgeries: [...(pad.advisedSurgeries || [])],
    diagnosis: pad.diagnosis || '',
    chiefComplaint: pad.chiefComplaint || '',
    doctorNotes: pad.doctorNotes || '',
    followUpRequired: !!pad.followUpRequired,
    followUpIn: pad.followUpIn || null,
  };
  if (!template) return { next, summary };

  // Symptoms — union onto the selection.
  const selected = new Set(next.symptoms);
  for (const raw of template.symptoms || []) {
    const s = String(raw || '').trim();
    if (!s) continue;
    if (selected.has(s)) { summary.symptoms.skipped += 1; continue; }
    selected.add(s);
    next.symptoms.push(s);
    // Outside the known lists → stored as custom, identical to typing it.
    if (!knownSymptoms.has(s) && !next.customSymptoms.includes(s)) next.customSymptoms.push(s);
    summary.symptoms.added += 1;
  }

  // Medicines — append, skip name+strength duplicates.
  const medSeen = new Set(next.medicines.map(medicineKey));
  let seq = 0;
  for (const m of template.medicines || []) {
    if (!m || !(m.medicine || m.medicine_input)) continue;
    const key = medicineKey(m);
    if (medSeen.has(key)) { summary.medicines.skipped += 1; continue; }
    medSeen.add(key);
    next.medicines.push(templateMedicineRow(m, seq++));
    summary.medicines.added += 1;
  }

  // Tests — append, skip duplicates via the SAME matcher the Tests row uses.
  const testSeen = new Set(next.investigations.map((n) => normalizeLabName(n)));
  for (const raw of template.investigations || []) {
    const name = String(raw || '').trim();
    if (!name) continue;
    const key = normalizeLabName(name);
    if (testSeen.has(key)) {
      summary.tests.skipped += 1;
      summary.tests.skippedNames.push(name);
      continue;
    }
    testSeen.add(key);
    next.investigations.push(name);
    summary.tests.added += 1;
  }

  // Surgeries (FE-8) — append, skip duplicates via `normaliseRowKey`, the SAME
  // row identity the Advised surgeries section itself uses (FE-6) — never this
  // module's own `norm()`, which is the medicines/tests key.
  const surgerySeen = new Set(next.advisedSurgeries.map(normaliseRowKey));
  for (const raw of template.advised_surgeries || []) {
    const s = String(raw || '').trim();
    if (!s) continue;
    const key = normaliseRowKey(s);
    if (surgerySeen.has(key)) { summary.surgeries.skipped += 1; continue; }
    surgerySeen.add(key);
    next.advisedSurgeries.push(s);
    summary.surgeries.added += 1;
  }

  // Diagnosis — set if empty, else append; skip an exact repeat.
  const dx = String(template.diagnosis || '').trim();
  if (dx) {
    const current = next.diagnosis.trim();
    if (!current) { next.diagnosis = dx; summary.diagnosis = 'added'; }
    else if (current.toLowerCase().includes(dx.toLowerCase())) { summary.diagnosis = 'skipped'; }
    else { next.diagnosis = `${current}, ${dx}`; summary.diagnosis = 'added'; }
  }

  // Chief complaint / notes / follow-up — fill ONLY if empty.
  const cc = String(template.chief_complaint || '').trim();
  if (cc && !next.chiefComplaint.trim()) { next.chiefComplaint = cc; summary.chiefComplaint = true; }

  const notes = String(template.doctor_notes || '').trim();
  if (notes && !next.doctorNotes.trim()) { next.doctorNotes = notes; summary.notes = true; }

  if (template.follow_up_required && !next.followUpRequired) {
    next.followUpRequired = true;
    next.followUpIn = template.follow_up_in || null;
    summary.followUp = true;
  }

  summary.changed = summary.medicines.added > 0 || summary.symptoms.added > 0
    || summary.tests.added > 0 || summary.surgeries.added > 0 || summary.diagnosis === 'added'
    || summary.chiefComplaint || summary.notes || summary.followUp;
  return { next, summary };
}

/** Fold several templates onto one pad in ticked order (the multi-add path). */
export function mergeTemplates(pad, templates, opts = {}) {
  let next = pad;
  const summaries = [];
  for (const t of templates || []) {
    const res = mergeTemplate(next, t, opts);
    next = res.next;
    summaries.push(res.summary);
  }
  return { next, summaries };
}

// ── Toast copy (PO, verbatim) ────────────────────────────────────────

// The one irregular plural this module needs — everything else is a plain `+s`.
const IRREGULAR_PLURALS = { surgery: 'surgeries' };
const plural = (n, word) => `${n} ${n === 1 ? word : (IRREGULAR_PLURALS[word] || `${word}s`)}`;

function kindsAdded(s) {
  const kinds = [];
  if (s.medicines.added) kinds.push('medicines');
  if (s.symptoms.added) kinds.push('symptoms');
  if (s.tests.added) kinds.push('tests');
  if (s.surgeries.added) kinds.push('surgeries');
  if (s.diagnosis === 'added') kinds.push('diagnosis');
  if (s.chiefComplaint) kinds.push('chiefComplaint');
  if (s.notes) kinds.push('notes');
  if (s.followUp) kinds.push('followUp');
  return kinds;
}

function skippedCount(s) {
  return s.medicines.skipped + s.symptoms.skipped + s.tests.skipped + s.surgeries.skipped
    + (s.diagnosis === 'skipped' ? 1 : 0);
}

/** ONE toast per Add, summarising across every applied template. */
export function buildAddToastMessage(summaries) {
  const list = summaries || [];
  if (list.length === 0) return '';
  const skipped = list.reduce((n, s) => n + skippedCount(s), 0);

  if (list.length === 1) {
    const s = list[0];
    const kinds = kindsAdded(s);
    if (kinds.length === 0) return `Everything in "${s.templateName}" is already on this prescription.`;
    if (kinds.length === 1) {
      const kind = kinds[0];
      if (kind === 'medicines') {
        const head = `Added ${plural(s.medicines.added, 'medicine')} from "${s.templateName}".`;
        return s.medicines.skipped
          ? `${head} ${s.medicines.skipped} ${s.medicines.skipped === 1 ? 'was' : 'were'} already on this prescription.`
          : head;
      }
      if (kind === 'symptoms') {
        return s.symptoms.skipped
          ? `Added ${plural(s.symptoms.added, 'symptom')}. ${s.symptoms.skipped} ${s.symptoms.skipped === 1 ? 'was' : 'were'} already selected.`
          : `Added ${plural(s.symptoms.added, 'symptom')} from "${s.templateName}".`;
      }
      if (kind === 'tests') {
        const head = `Added ${plural(s.tests.added, 'lab test')} from "${s.templateName}".`;
        if (!s.tests.skipped) return head;
        return s.tests.skipped === 1
          ? `${head} ${s.tests.skippedNames[0]} was already listed.`
          : `${head} ${s.tests.skipped} were already listed.`;
      }
      if (kind === 'diagnosis') return 'Added to the diagnosis.';
      if (kind === 'surgeries') {
        const head = `Added ${plural(s.surgeries.added, 'surgery')} from "${s.templateName}".`;
        return s.surgeries.skipped
          ? `${head} ${s.surgeries.skipped} ${s.surgeries.skipped === 1 ? 'was' : 'were'} already listed.`
          : head;
      }
    }
  }

  const n = list.length;
  const head = `Added ${plural(n, 'template')}`;
  return skipped
    ? `${head}. ${plural(skipped, 'item')} ${skipped === 1 ? 'was' : 'were'} already on this prescription.`
    : `${head} to this prescription.`;
}

// ── Write payloads ───────────────────────────────────────────────────

/**
 * buildTemplatePayload — the ONLY builder for a create/update body. Read-only
 * fields (`template_id`, `usage_count`, `created_at`, `updated_at`) are never
 * echoed back: the request schema is `extra="forbid"`, so one of them turns a
 * save into a 422. `category` is create-only (immutable server-side).
 */
export function buildTemplatePayload({ name, category = null, parts = {} }, { forUpdate = false } = {}) {
  const payload = {
    name: String(name || '').trim(),
    symptoms: parts.symptoms || [],
    medicines: (parts.medicines || []).map((m) => ({
      medicine: m.medicine || m.medicine_input || '',
      medicine_input: m.medicine_input || null,
      strength: m.strength || null,
      form: m.form || null,
      item_type: m.item_type || 'medicine',
      dosage_schedule: m.dosage_schedule || null,
      frequency: m.frequency || null,
      timing: m.timing || null,
      duration: m.duration || null,
      notes: m.notes || null,
      is_resolved: m.is_resolved ?? true,
    })),
    chief_complaint: (parts.chiefComplaint || '').trim() || null,
    diagnosis: (parts.diagnosis || '').trim() || null,
    doctor_notes: (parts.doctorNotes || '').trim() || null,
    investigations: parts.investigations || [],
    lab_catalog_ids: parts.labCatalogIds || [],
    advised_surgeries: parts.advisedSurgeries || [],
    follow_up_required: !!parts.followUpRequired,
    follow_up_in: parts.followUpRequired ? (parts.followUpIn || null) : null,
  };
  if (!forUpdate && category) payload.category = category;
  return payload;
}

/** Row sub-line in the list — what this template actually holds. */
export function templatePreviewLine(template) {
  const key = categoryKeyOf(template);
  if (key === TEMPLATE_CATEGORIES.SYMPTOMS) return (template.symptoms || []).join(' · ');
  if (key === TEMPLATE_CATEGORIES.DIAGNOSIS) return template.diagnosis || '';
  if (key === TEMPLATE_CATEGORIES.MEDICINES) {
    return (template.medicines || []).map((m) => m.medicine || m.medicine_input).filter(Boolean).join(' · ');
  }
  if (key === TEMPLATE_CATEGORIES.LAB_TESTS) return (template.investigations || []).join(' · ');
  if (key === TEMPLATE_CATEGORIES.SURGERIES) return (template.advised_surgeries || []).join(' · ');
  return templateContentSummary(template);
}

/**
 * What a template holds, counted — "3 medicines · diagnosis". Read by the
 * whole-Rx preview line above AND by every chip's tooltip/accessible name, so
 * the pad and the list describe the same template the same way.
 */
export function templateContentSummary(template) {
  const parts = [];
  if (template?.symptoms?.length) parts.push(plural(template.symptoms.length, 'symptom'));
  if (template?.medicines?.length) parts.push(plural(template.medicines.length, 'medicine'));
  if (template?.investigations?.length) parts.push(plural(template.investigations.length, 'test'));
  if (template?.advised_surgeries?.length) parts.push(plural(template.advised_surgeries.length, 'surgery'));
  if (template?.diagnosis) parts.push('diagnosis');
  if (template?.chief_complaint) parts.push('chief complaint');
  if (template?.doctor_notes) parts.push('notes');
  return parts.join(' · ');
}
